import React, { FC, useCallback, useContext, useState } from 'react';

import { RadioButton } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { updateSkillFlags } from 'client/api';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { Channel, DeveloperType, LookType, MonitoringType, Skill } from 'client/model/skill';
import * as settingsKeyset from 'i18n/Settings';
import * as keyset from 'i18n/SkillPublishing';

import styles from './AdminSettings.module.scss';

const t = i18n(keyset);
const tSettings = i18n(settingsKeyset);

type BoolFlags = keyof Pick<
    Skill,
    | 'exposeInternalFlags'
    | 'isRecommended'
    | 'openInNewTab'
    | 'isVip'
    | 'useZora'
    | 'isTrustedSmartHomeSkill'
    | 'safeForKids'
>;

type EnumFlags = keyof Pick<Skill, 'monitoringType' | 'developerType' | 'look'>;
type SkillFlag = EnumFlags | BoolFlags;

interface FlagProps<T extends SkillFlag> {
    name: T;
    title: string;
    onChange: (flag: T, value: Skill[T]) => void;
    value: Skill[T];
    disabled?: boolean;
    fullWidth?: boolean;
}

interface BoolFlagProps extends Omit<FlagProps<BoolFlags>, 'value' | 'onChange'> {
    value: boolean;
    onChange: (flag: BoolFlags, value: boolean) => void;
    trueLabel: string;
    falseLabel: string;
}

interface EnumFlagProps<T extends EnumFlags> extends FlagProps<T> {
    values: Record<string, string>;
}

const BoolFlag: FC<BoolFlagProps> = ({ name, title, value, onChange, trueLabel, falseLabel, disabled }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        onChange(name, e.target.value === 'true');
    };

    return (
        <Field fieldTitle={title} containerClassName={styles.adminSettingsField}>
            <RadioButton
                name={name}
                className={styles.adminSettingsFieldContent}
                value={String(value)}
                onChange={handleChange}
                size="l"
                disabled={disabled}
            >
                <RadioButton.Option value="true">{trueLabel}</RadioButton.Option>
                <RadioButton.Option value="false">{falseLabel}</RadioButton.Option>
            </RadioButton>
        </Field>
    );
};

const EnumFlag = <T extends EnumFlags>({
    name,
    title,
    value,
    values,
    onChange,
    fullWidth,
    disabled,
}: EnumFlagProps<T>) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        onChange(name, e.target.value as Skill[T]);
    };

    return (
        <Field fieldTitle={title} containerClassName={styles.adminSettingsField}>
            <RadioButton
                name={name}
                className={clx(styles.adminSettingsFieldContent, fullWidth && styles.adminSettingsFieldContent_full)}
                value={String(value)}
                onChange={handleChange}
                size="l"
                disabled={disabled}
            >
                {Object.entries(values).map(([key, label]) => (
                    <RadioButton.Option key={key} value={key}>
                        {label}
                    </RadioButton.Option>
                ))}
            </RadioButton>
        </Field>
    );
};

export const AdminSettings: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => {
    const context = useContext(CurrentAliceAppContext);

    const [isFlagUpdating, setIsFlagUpdating] = useState<Record<SkillFlag, boolean>>({
        developerType: false,
        exposeInternalFlags: false,
        isRecommended: false,
        isVip: false,
        look: false,
        monitoringType: false,
        openInNewTab: false,
        useZora: false,
        isTrustedSmartHomeSkill: false,
        safeForKids: false,
    });

    const onFlagUpdate = useCallback(
        async (skillId: string, flag: SkillFlag, patch: Partial<Skill>) => {
            setIsFlagUpdating((prev) => ({
                ...prev,
                [flag]: true,
            }));

            try {
                await updateSkillFlags(skillId, patch);
                await context?.refreshAliceApp();
            } catch (e) {
                console.error(e);
            } finally {
                setIsFlagUpdating((prev) => ({
                    ...prev,
                    [flag]: false,
                }));
            }
        },
        [context],
    );

    if (!context?.aliceApp) {
        return <PageLoader />;
    }

    const skill = context.aliceApp;

    const handleFlagChange = <T extends SkillFlag>(flag: T, value: Skill[T]) => {
        onFlagUpdate(skill.id, flag, { [flag]: value });
    };

    const getBoolFlagProps = (name: BoolFlags) => ({
        name,
        value: skill[name],
        disabled: disabled || isFlagUpdating[name],
        onChange: handleFlagChange,
    });

    const getEnumFlagProps = <T extends EnumFlags>(name: T) => ({
        name,
        value: skill[name],
        disabled: disabled || isFlagUpdating[name],
        onChange: handleFlagChange,
    });

    return (
        <>
            <header className={styles.adminSettingsHeader}>
                <div>
                    <h1 className={styles.skillTitle}>{context?.aliceApp?.draft?.name}</h1>
                </div>
            </header>
            <div className={styles.content}>
                <h3 className={styles.adminSettingsTitle}>{tSettings('admin-settings')}</h3>

                <BoolFlag
                    title={t('recommended-skill')}
                    falseLabel={t('no')}
                    trueLabel={t('yes')}
                    {...getBoolFlagProps('isRecommended')}
                />

                <EnumFlag
                    title={t('developer-type')}
                    values={{
                        [DeveloperType.External]: t('external'),
                        [DeveloperType.Yandex]: t('yandex'),
                    }}
                    {...getEnumFlagProps('developerType')}
                />

                <BoolFlag
                    title={t('trusted-skill')}
                    falseLabel={t('no')}
                    trueLabel={t('yes')}
                    {...getBoolFlagProps('isTrustedSmartHomeSkill')}
                />

                {skill.channel === Channel.AliceSkill && (
                    <>
                        <BoolFlag
                            title={t('title-access-web-hook')}
                            falseLabel={t('checkbox-directly')}
                            trueLabel={t('checkbox-zora')}
                            {...getBoolFlagProps('useZora')}
                        />

                        <BoolFlag
                            title={t('title-experimental')}
                            falseLabel={t('hide')}
                            trueLabel={t('show')}
                            {...getBoolFlagProps('exposeInternalFlags')}
                        />

                        <BoolFlag
                            title={t('title-open-new-tab')}
                            falseLabel={t('no')}
                            trueLabel={t('yes')}
                            {...getBoolFlagProps('openInNewTab')}
                        />

                        <BoolFlag
                            title={t('title-safe-kids')}
                            falseLabel={t('no')}
                            trueLabel={t('yes')}
                            {...getBoolFlagProps('safeForKids')}
                        />

                        <EnumFlag
                            title={t('monitoring-group')}
                            values={{
                                [MonitoringType.Yandex]: t('yandex'),
                                [MonitoringType.JustAI]: 'JustAI',
                                [MonitoringType.Nonmonitored]: t('no-monitoring'),
                            }}
                            fullWidth
                            {...getEnumFlagProps('monitoringType')}
                        />

                        <EnumFlag
                            title={t('appearance')}
                            values={{
                                [LookType.External]: t('external'),
                                [LookType.Internal]: t('internal'),
                            }}
                            {...getEnumFlagProps('look')}
                        />
                    </>
                )}
            </div>
        </>
    );
};
